/* eslint-disable */
import { useEffect, useState, useRef } from "react";
import {
  Upload,
  Globe,
  Globe2,
  Lock,
  CheckCircle2,
  FileText,
  X,
  File as FileIcon,
  Folder,
} from "lucide-react";
import { docsApi, iconFor, formatFileSize } from "./api";

const SUGGESTED_TAGS = [
  "budget", "quarterly", "2026", "annual", "financial", "rules", "bylaws",
  "maintenance", "repair", "pool", "clubhouse", "event", "important", "gbm",
  "minutes", "agenda",
];

const ALLOWED_EXT = new Set([
  "pdf", "doc", "docx", "xls", "xlsx", "ppt", "pptx", "txt", "png", "jpg", "jpeg",
]);
const MAX_SIZE = 10 * 1024 * 1024;

function validFile(f) {
  const ext = (f.name || "").split(".").pop().toLowerCase();
  if (!ALLOWED_EXT.has(ext)) return `${f.name}: unsupported type`;
  if (f.size > MAX_SIZE) return `${f.name}: exceeds 10MB`;
  return null;
}

export default function UploadModal({ open, onClose, isAdmin, onUploaded }) {
  const [mode, setMode] = useState("single"); // single | bundle
  const [title, setTitle] = useState("");
  const [bundleName, setBundleName] = useState("");
  const [description, setDescription] = useState("");
  const [files, setFiles] = useState([]); // File[]
  const [category, setCategory] = useState("");
  const [categories, setCategories] = useState([]);
  const [tags, setTags] = useState([]);
  const [tagInput, setTagInput] = useState("");
  const [visibility, setVisibility] = useState("PUBLIC");
  const [internetRequested, setInternetRequested] = useState(false);
  const [showOptional, setShowOptional] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [progress, setProgress] = useState(null); // {done,total}
  const [error, setError] = useState("");
  const [confirmation, setConfirmation] = useState(null);
  const inputRef = useRef(null);

  // Load the live category list whenever the modal opens.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      try {
        const data = await docsApi.listCategories();
        if (cancelled) return;
        const list = data.categories || [];
        setCategories(list);
        setCategory((cur) => {
          if (cur && list.some((c) => (c.key || c.name) === cur)) return cur;
          const other = list.find((c) => (c.key || c.name) === "OTHER");
          return (other && (other.key || other.name)) || (list[0] && (list[0].key || list[0].name)) || "OTHER";
        });
      } catch (_) {
        /* keep whatever we have */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open]);

  // Hooks above always run; safe to bail here (see DocumentCard/UploadModal
  // hook-order history).
  if (!open) return null;

  const reset = () => {
    setMode("single");
    setTitle("");
    setBundleName("");
    setDescription("");
    setFiles([]);
    setTags([]);
    setTagInput("");
    setVisibility("PUBLIC");
    setInternetRequested(false);
    setShowOptional(false);
    setDragOver(false);
    setError("");
    setProgress(null);
    setConfirmation(null);
    setSubmitting(false);
  };

  const close = () => {
    reset();
    onClose();
  };

  const addFiles = (incoming) => {
    setError("");
    const arr = Array.from(incoming || []);
    const bad = arr.map(validFile).find(Boolean);
    if (bad) setError(bad);
    const ok = arr.filter((f) => !validFile(f));
    if (!ok.length) return;
    if (mode === "single") {
      setFiles([ok[0]]);
      if (!title.trim()) setTitle(ok[0].name.replace(/\.[^.]+$/, ""));
    } else {
      setFiles((prev) => [...prev, ...ok]);
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    addFiles(e.dataTransfer.files);
  };

  const removeFile = (idx) =>
    setFiles((prev) => prev.filter((_, i) => i !== idx));

  const addTag = (t) => {
    const tag = t.trim().toLowerCase();
    if (tag && !tags.includes(tag)) setTags([...tags, tag]);
    setTagInput("");
  };

  const switchMode = (m) => {
    setMode(m);
    setError("");
    // Going single→bundle keeps files; bundle→single trims to the first.
    if (m === "single") setFiles((prev) => prev.slice(0, 1));
  };

  const submit = async () => {
    setError("");
    if (!category) return setError("Pick a category");
    if (files.length === 0) return setError("Add at least one file");

    if (mode === "single") {
      if (!title.trim()) return setError("Title is required");
      setSubmitting(true);
      try {
        const doc = await docsApi.upload({
          file: files[0],
          title,
          description,
          category,
          tags,
          visibility,
          request_internet_open: visibility === "PUBLIC" && internetRequested,
        });
        const pending = doc.status === "PENDING" && visibility !== "PRIVATE";
        setConfirmation({
          title: visibility === "PRIVATE" ? "Document saved" : "Document uploaded",
          message:
            visibility === "PRIVATE"
              ? "Saved privately — only visible to you."
              : pending
              ? "Awaiting board approval to become visible."
              : "Document is now visible to the community.",
          color: visibility === "PRIVATE" ? "gray" : pending ? "amber" : "green",
        });
        onUploaded && onUploaded();
      } catch (e) {
        setError(e.message);
      } finally {
        setSubmitting(false);
      }
      return;
    }

    // Bundle mode: create the bundle, then upload every file into it.
    if (!bundleName.trim()) return setError("Bundle name is required");
    setSubmitting(true);
    setProgress({ done: 0, total: files.length });
    try {
      const bundle = await docsApi.createBundle({
        name: bundleName.trim(),
        category,
        description: description.trim() || undefined,
      });
      const results = await docsApi.uploadMany({
        files,
        category,
        visibility,
        bundle_id: bundle.id,
        tags,
        request_internet_open: visibility === "PUBLIC" && internetRequested,
        onProgress: (done, total) => setProgress({ done, total }),
      });
      const failed = results.filter((r) => !r.ok);
      const okCount = results.length - failed.length;
      setConfirmation({
        title: failed.length ? "Bundle partially uploaded" : "Bundle uploaded",
        message: failed.length
          ? `${okCount} of ${results.length} files uploaded. ${failed.length} failed.`
          : `“${bundleName.trim()}” created with ${okCount} file${okCount === 1 ? "" : "s"}.`,
        color: failed.length ? "amber" : "green",
      });
      onUploaded && onUploaded();
    } catch (e) {
      setError(e.message || "Could not create bundle");
    } finally {
      setSubmitting(false);
      setProgress(null);
    }
  };

  const canSubmit =
    !submitting &&
    category &&
    files.length > 0 &&
    (mode === "single" ? title.trim() : bundleName.trim());

  return (
    <div className="docs-modal-overlay" onClick={close}>
      <div className="docs-modal" onClick={(e) => e.stopPropagation()}>
        {confirmation ? (
          <div className="docs-modal-confirm">
            <div className={`docs-confirm-icon docs-confirm-${confirmation.color}`}>
              <CheckCircle2 size={30} strokeWidth={1.6} />
            </div>
            <h2>{confirmation.title}</h2>
            <p>{confirmation.message}</p>
            <div className="docs-confirm-actions">
              <button className="docs-btn-secondary" onClick={close}>
                Close
              </button>
              <button
                className="docs-btn-primary docs-btn-auto"
                onClick={reset}
              >
                Upload Another
              </button>
            </div>
          </div>
        ) : (
          <>
            <div className="docs-modal-header">
              <h2>Upload to Documents</h2>
              <button className="docs-modal-close" onClick={close}>×</button>
            </div>

            <div className="docs-modal-body">
              {/* Mode toggle */}
              <div className="docs-mode-toggle">
                <button
                  className={mode === "single" ? "active" : ""}
                  onClick={() => switchMode("single")}
                  type="button"
                >
                  <FileText size={15} /> Single document
                </button>
                <button
                  className={mode === "bundle" ? "active" : ""}
                  onClick={() => switchMode("bundle")}
                  type="button"
                >
                  <Folder size={15} /> Bundle (multiple files)
                </button>
              </div>

              {/* Name */}
              {mode === "single" ? (
                <div className="docs-field">
                  <label>Title <span className="req">*</span></label>
                  <input
                    type="text"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder="Enter document title"
                  />
                </div>
              ) : (
                <div className="docs-field">
                  <label>Bundle name <span className="req">*</span></label>
                  <input
                    type="text"
                    value={bundleName}
                    onChange={(e) => setBundleName(e.target.value)}
                    placeholder='e.g. "Q4 2026 Invoices"'
                  />
                </div>
              )}

              {/* Dropzone */}
              <div
                className={`docs-dropzone ${dragOver ? "drag" : ""}`}
                onClick={() => inputRef.current && inputRef.current.click()}
                onDragOver={(e) => {
                  e.preventDefault();
                  setDragOver(true);
                }}
                onDragLeave={() => setDragOver(false)}
                onDrop={onDrop}
                role="button"
                tabIndex={0}
              >
                <input
                  ref={inputRef}
                  type="file"
                  multiple={mode === "bundle"}
                  hidden
                  onChange={(e) => {
                    addFiles(e.target.files);
                    e.target.value = "";
                  }}
                />
                <div className="docs-dropzone-icon">
                  <Upload size={26} strokeWidth={1.7} />
                </div>
                <p className="docs-dropzone-main">
                  <strong>Drag &amp; drop</strong>{" "}
                  {mode === "bundle" ? "files" : "a file"} here, or click to browse
                </p>
                <p className="docs-dropzone-hint">
                  PDF, DOC, XLS, PPT, TXT, images · max 10MB each
                </p>
              </div>

              {/* Selected files */}
              {files.length > 0 && (
                <div className="docs-filelist">
                  {files.map((f, i) => (
                    <div className="docs-fileitem" key={`${f.name}-${i}`}>
                      <FileIcon size={15} />
                      <span className="docs-fileitem-name">{f.name}</span>
                      <span className="docs-fileitem-size">
                        {formatFileSize(f.size)}
                      </span>
                      <button
                        type="button"
                        className="docs-fileitem-remove"
                        onClick={() => removeFile(i)}
                        aria-label={`Remove ${f.name}`}
                      >
                        <X size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* Description (single = document, bundle = folder) */}
              <div className="docs-field">
                <label>
                  {mode === "bundle" ? "Bundle description" : "Description"}{" "}
                  <span className="docs-opt">(optional)</span>
                </label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={2}
                  placeholder={
                    mode === "bundle"
                      ? "What's in this bundle?"
                      : "Add a short description…"
                  }
                />
              </div>

              {/* Category chips */}
              <div className="docs-field">
                <label>Category <span className="req">*</span></label>
                <div className="docs-cat-chips">
                  {categories.map((c) => {
                    const key = c.key || c.name;
                    const Ic = iconFor(c.icon);
                    const selected = category === key;
                    return (
                      <button
                        key={key}
                        type="button"
                        className={`docs-cat-chip ${selected ? "selected" : ""}`}
                        style={
                          selected
                            ? { borderColor: c.color, background: `${c.color}14`, color: c.color }
                            : null
                        }
                        onClick={() => setCategory(key)}
                      >
                        <Ic size={14} /> {c.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Visibility */}
              <div className="docs-field">
                <label>Visibility</label>
                <div className="docs-vis-row">
                  <button
                    type="button"
                    className={`docs-vis-pill ${visibility === "PUBLIC" ? "selected" : ""}`}
                    onClick={() => setVisibility("PUBLIC")}
                  >
                    <Globe size={15} /> Public
                    <small>{isAdmin ? "All members" : "All members (after approval)"}</small>
                  </button>
                  <button
                    type="button"
                    className={`docs-vis-pill ${visibility === "PRIVATE" ? "selected" : ""}`}
                    onClick={() => setVisibility("PRIVATE")}
                  >
                    <Lock size={15} /> Private
                    <small>Only you</small>
                  </button>
                </div>
                {visibility === "PUBLIC" && (
                  <label className="docs-internet-opt">
                    <input
                      type="checkbox"
                      checked={internetRequested}
                      onChange={(e) => setInternetRequested(e.target.checked)}
                    />
                    <span>
                      <span className="docs-internet-title">
                        <Globe2 size={14} /> Also publish to the public internet
                      </span>
                      <small>
                        {isAdmin
                          ? "Anyone can download it without logging in."
                          : "A board admin reviews and approves this before it goes public."}
                      </small>
                    </span>
                  </label>
                )}
              </div>

              {/* Optional details */}
              <button
                type="button"
                className="docs-optional-toggle"
                onClick={() => setShowOptional((v) => !v)}
              >
                {showOptional ? "− Hide" : "+ Add"} tags
              </button>
              {showOptional && (
                <>
                  <div className="docs-field">
                    <label>Tags</label>
                    <div className="docs-tag-input-row">
                      <input
                        type="text"
                        value={tagInput}
                        onChange={(e) => setTagInput(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            addTag(tagInput);
                          }
                        }}
                        placeholder="Type and press Enter"
                      />
                      <button
                        type="button"
                        className="docs-btn-primary docs-btn-auto"
                        onClick={() => addTag(tagInput)}
                      >
                        Add
                      </button>
                    </div>
                    {tags.length > 0 && (
                      <div className="docs-tag-chips">
                        {tags.map((t) => (
                          <span key={t} className="docs-tag-chip">
                            {t}
                            <button type="button" onClick={() => setTags(tags.filter((x) => x !== t))}>
                              ×
                            </button>
                          </span>
                        ))}
                      </div>
                    )}
                    <div className="docs-suggested">
                      {SUGGESTED_TAGS.filter((t) => !tags.includes(t))
                        .slice(0, 8)
                        .map((t) => (
                          <button
                            type="button"
                            key={t}
                            className="docs-suggested-tag"
                            onClick={() => addTag(t)}
                          >
                            {t}
                          </button>
                        ))}
                    </div>
                  </div>
                </>
              )}

              {error && <div className="docs-error">{error}</div>}
            </div>

            <div className="docs-modal-actions">
              <button className="docs-btn-secondary" onClick={close}>
                Cancel
              </button>
              <button
                className="docs-btn-primary docs-btn-auto"
                disabled={!canSubmit}
                onClick={submit}
              >
                {submitting
                  ? progress
                    ? `Uploading ${progress.done}/${progress.total}…`
                    : "Uploading…"
                  : mode === "bundle"
                  ? `Upload ${files.length || ""} ${files.length === 1 ? "file" : "files"}`.trim()
                  : "Upload Document"}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
